<template>
  <div class="scan-page">
    <div class="scans-head">
      <p class="scans-intro">
        Each scan is one walkdown video. Duallo processes it into a point cloud and runs detection;
        add it to a <strong>version</strong> to see it in the 3D viewer.
      </p>

      <button type="button" class="new-scan-btn" @click="openScan">
        <v-icon size="18">mdi-tray-arrow-up</v-icon>
        <span>Upload walkdown</span>
      </button>
    </div>

    <v-dialog v-model="scanDialog" max-width="760" persistent scrollable>
      <div class="modal scan-modal" :class="{ 'scan-modal--flying': closing }">
        <header class="modal-head">
          <div class="modal-heading">
            <div class="modal-eyebrow">New scan</div>
            <h2 class="modal-title">{{ handedOff ? 'Uploading in the background' : 'Upload a walkdown' }}</h2>
          </div>
          <div class="modal-head-right">
            <span v-if="!handedOff" class="step-badge">Step {{ currentIndex + 1 }} / {{ flow.length }}</span>
            <button v-if="!handedOff" type="button" class="modal-close" aria-label="Close" @click="cancelScan">
              <v-icon size="20">mdi-close</v-icon>
            </button>
          </div>
        </header>

        <div v-if="!handedOff" class="step-progress" aria-hidden="true">
            <div v-for="(s, i) in flow" :key="s" class="step-dot"
              :class="{ 'is-active': i <= currentIndex, 'is-current': s === step }">
              <span class="step-dot-num">{{ i + 1 }}</span>
              <span class="step-dot-label">{{ stepLabelMap[s] }}</span>
            </div>
          </div>

          <v-window v-if="!handedOff" v-model="step" class="scan-stepper">
            <v-window-item :value="1">
                <div class="step-card">
                  <header class="step-head">
                    <div class="step-eyebrow">Step 1</div>
                    <h3 class="step-title">Enter scan details</h3>
                    <p class="step-description">
                      Give the scan a clear name and short description so it is easy to identify later.
                    </p>
                  </header>

                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field label="Scan Name" v-model="scanName" variant="outlined" density="comfortable"
                        hide-details="auto" prepend-inner-icon="mdi-tag-outline" />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field label="Scan Description" v-model="scanDescription" variant="outlined"
                        density="comfortable" hide-details="auto" prepend-inner-icon="mdi-text-box-outline" />
                    </v-col>
                  </v-row>

                  <div class="helper-card">
                    <v-icon size="18" color="#64ffda">mdi-lightbulb-outline</v-icon>
                    <span>Use names that describe the area, unit, or route captured.</span>
                  </div>

                  <div class="step-actions">
                    <v-btn class="ghost-btn" @click="cancelScan">Close</v-btn>
                    <LoadingButton :disabled="!scanName || !scanDescription" @click="step = 2"
                      buttonText="Next" customClass="primary-btn" />
                  </div>
                </div>
              </v-window-item>

              <v-window-item :value="2">
                <div class="step-card">
                  <header class="step-head">
                    <div class="step-eyebrow">Step 2</div>
                    <h3 class="step-title">Choose capture type</h3>
                    <p class="step-description">
                      Select the camera category that best matches the uploaded footage.
                    </p>
                  </header>

                  <div class="camera-grid">
                    <button type="button" class="camera-card" :class="{ 'is-selected': cameraType === '2d' }"
                      @click="selectCameraType('2d')">
                      <div class="camera-image-wrap">
                        <v-img :src="camera2D" class="camera-image" cover />
                        <span class="camera-pill">Single Lens</span>
                      </div>
                      <div class="camera-content">
                        <div class="camera-title-row">
                          <span class="camera-title">2D Camera</span>
                          <v-icon v-if="cameraType === '2d'" color="#64ffda">mdi-check-circle</v-icon>
                        </div>
                        <p class="camera-text">
                          Standard phone, GoPro, drone, or similar single lens device.
                        </p>
                        <div class="camera-meta">
                          <span class="camera-meta-chip">.mp4</span>
                          <span class="camera-meta-chip">.mov</span>
                          <span class="camera-meta-chip">.osv</span>
                        </div>
                      </div>
                    </button>

                    <button type="button" class="camera-card" :class="{ 'is-selected': cameraType === '360' }"
                      @click="selectCameraType('360')">
                      <div class="camera-image-wrap">
                        <v-img :src="camera360" class="camera-image" cover />
                        <span class="camera-pill">Dual Lens</span>
                      </div>
                      <div class="camera-content">
                        <div class="camera-title-row">
                          <span class="camera-title">360 Camera</span>
                          <v-icon v-if="cameraType === '360'" color="#64ffda">mdi-check-circle</v-icon>
                        </div>
                        <p class="camera-text">
                          Full surrounding capture for broader spatial context, typically with 2 lenses.
                        </p>
                        <div class="camera-meta">
                          <span class="camera-meta-chip">.insv</span>
                          <span class="camera-meta-chip">.osv</span>
                        </div>
                      </div>
                    </button>
                  </div>

                  <div class="step-actions">
                    <v-btn class="ghost-btn" @click="step = 1">Back</v-btn>
                  </div>
                </div>
              </v-window-item>

              <!-- All camera types: how was it captured? (walkthrough vs drone, single + dual lens) -->
              <v-window-item :value="3">
                <div class="step-card">
                  <header class="step-head">
                    <div class="step-eyebrow">Step {{ currentIndex + 1 }}</div>
                    <h3 class="step-title">How was it captured?</h3>
                    <p class="step-description">
                      Tell us whether this capture was recorded on a walkthrough or from a drone.
                    </p>
                  </header>

                  <div class="camera-grid">
                    <button type="button" class="camera-card capture-card" :class="{ 'is-selected': captureMethod === 'Walkthrough' }"
                      @click="captureMethod = 'Walkthrough'">
                      <div class="capture-body">
                        <v-icon size="40" :color="captureMethod === 'Walkthrough' ? '#64ffda' : '#8aa0aa'">mdi-walk</v-icon>
                        <div class="camera-title-row">
                          <span class="camera-title">Walkthrough</span>
                          <v-icon v-if="captureMethod === 'Walkthrough'" color="#64ffda">mdi-check-circle</v-icon>
                        </div>
                        <p class="camera-text">Handheld or body-mounted capture recorded on foot.</p>
                      </div>
                    </button>

                    <button type="button" class="camera-card capture-card" :class="{ 'is-selected': captureMethod === 'Drone' }"
                      @click="captureMethod = 'Drone'">
                      <div class="capture-body">
                        <v-icon size="40" :color="captureMethod === 'Drone' ? '#64ffda' : '#8aa0aa'">mdi-quadcopter</v-icon>
                        <div class="camera-title-row">
                          <span class="camera-title">Drone</span>
                          <v-icon v-if="captureMethod === 'Drone'" color="#64ffda">mdi-check-circle</v-icon>
                        </div>
                        <p class="camera-text">Aerial capture recorded from a drone-mounted camera.</p>
                      </div>
                    </button>
                  </div>

                  <div class="step-actions">
                    <v-btn class="ghost-btn" @click="step = 2">Back</v-btn>
                    <v-btn class="primary-btn" :disabled="!captureMethod" @click="step = 4">Next</v-btn>
                  </div>
                </div>
              </v-window-item>

              <v-window-item :value="4">
                <div class="step-card">
                  <header class="step-head">
                    <div class="step-eyebrow">Step {{ currentIndex + 1 }}</div>
                    <h3 class="step-title">Upload source file</h3>
                    <p class="step-description">Upload the original source capture for this scan.</p>
                  </header>

                  <div class="upload-panel">
                    <div class="upload-layout" :class="{ 'has-file': selectedFile }">
                      <!-- LEFT: preview on top, then the file picker below it. -->
                      <div class="upload-left">
                        <!-- Capture preview. 5 frames evenly spread across the video; click a thumbnail to
                             view it. The frame hugs the media aspect (portrait / landscape / 360 all fit). -->
                        <div v-if="selectedFile" class="upload-preview">
                          <div class="preview-frame">
                            <!-- Live, smooth preview: the real video blitted to a canvas (rotation-correct). -->
                            <canvas v-show="previewVideoReady" ref="previewCanvas" class="preview-canvas"></canvas>

                            <!-- First frame + "building" message until the live preview is ready. -->
                            <template v-if="!previewVideoReady">
                              <img v-if="posterUrl" :src="posterUrl" class="preview-img" alt="Capture preview" />
                              <div v-else-if="captureThumbLoading" class="preview-shimmer"></div>
                              <div v-else class="preview-empty">
                                <v-icon size="30" color="#64ffda">mdi-file-video-outline</v-icon>
                                <span>No preview available for this file type</span>
                              </div>
                              <div v-if="previewBuilding" class="preview-building">
                                <v-progress-circular indeterminate size="15" width="2" color="#64ffda" />
                                <span>Building live preview…</span>
                              </div>
                            </template>

                            <button v-if="previewVideoReady" type="button" class="flip-toggle"
                              :title="previewPlaying ? 'Pause' : 'Play'" @click="togglePreview">
                              <v-icon size="18">{{ previewPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                            </button>
                          </div>

                          <div v-if="captureThumbLoading || hasAnyFrame" class="frame-strip">
                            <button v-for="t in segmentThumbs" :key="t.s" type="button" class="frame-thumb"
                              :class="{ active: t.s === curSeg, empty: !t.frame }"
                              :disabled="!t.frame" @click="jumpToSegment(t.s)">
                              <img v-if="t.frame" :src="t.frame.dataUrl" alt="" />
                              <div v-else class="frame-thumb-shimmer"></div>
                            </button>
                          </div>
                        </div>

                        <v-file-input v-model="selectedFile" label="Drag and drop or select file" class="upload-file-input"
                          :accept="acceptedFileTypes" prepend-icon="" prepend-inner-icon="mdi-cloud-upload-outline"
                          variant="outlined" density="comfortable" hide-details="auto" show-size
                          @update:model-value="onFileSelected" @click:clear="clearSelection" />

                        <div v-if="selectedFile" class="preview-caption">
                          <v-icon size="15" color="#64ffda">mdi-file-video-outline</v-icon>
                          <span class="preview-name">{{ selectedFile.name }}</span>
                          <span class="preview-size">{{ formatFileSize(selectedFile.size) }}</span>
                        </div>
                      </div>

                      <!-- RIGHT: capture quality + tips, so the user can act on a poor capture (swap / re-capture). -->
                      <div v-if="selectedFile" class="upload-side">
                        <CaptureQualityPanel :meta="captureMeta" :loading="captureMetaLoading"
                          :camera-type="cameraType === '360' ? '360' : '2d'" />
                        <CaptureTips class="upload-tips" />
                      </div>
                    </div>
                  </div>

                  <div class="step-actions">
                    <v-btn class="ghost-btn" @click="goToPrevious">Back</v-btn>
                    <v-btn class="primary-btn" :disabled="selectedFile == null" @click="step = 5">Next</v-btn>
                  </div>
                </div>
              </v-window-item>

              <v-window-item :value="5">
                <div class="step-card">
                  <header class="step-head">
                    <div class="step-eyebrow">Step {{ currentIndex + 1 }}</div>
                    <h3 class="step-title">Review and complete</h3>
                    <p class="step-description">
                      Confirm the scan details before saving and uploading the file.
                    </p>
                  </header>

                  <div class="confirm-review">
                    <div class="confirm-col-head">Summary</div>
                    <dl class="review-list">
                      <div class="review-row">
                        <dt>Scan name</dt>
                        <dd>{{ scanName || '—' }}</dd>
                      </div>
                      <div class="review-row">
                        <dt>Description</dt>
                        <dd>{{ scanDescription || '—' }}</dd>
                      </div>
                      <div class="review-row">
                        <dt>Camera</dt>
                        <dd>{{ cameraType === '2d' ? '2D Camera' : '360 Camera' }} · {{ captureMethod }}</dd>
                      </div>
                      <div class="review-row">
                        <dt>File</dt>
                        <dd class="review-file">{{ selectedFile?.name }}</dd>
                      </div>
                    </dl>

                    <!-- One-line recap; the full quality panel + tips are on the Upload step now. -->
                    <div class="confirm-recap" :class="`tone-${captureRecap.tone}`">
                      <span class="recap-dot"></span>
                      <span>{{ captureRecap.text }}</span>
                    </div>
                  </div>

                  <div class="step-actions">
                    <v-btn class="ghost-btn" @click="step = 4">Back</v-btn>
                    <v-btn class="primary-btn" @click="saveAndUpload">Save and Upload</v-btn>
                  </div>
                </div>
              </v-window-item>
          </v-window>

          <!-- Handed off: the upload now runs in the background (sidebar), so the user can close this. -->
          <div v-if="handedOff" class="handoff-card">
            <div class="handoff-icon"><v-icon size="30" color="#6ee7a0">mdi-cloud-check-outline</v-icon></div>
            <h3 class="handoff-title">Upload started</h3>
            <p class="handoff-text">
              “{{ handoffName }}” is uploading in the background. You can close this window — track its
              progress any time in the sidebar under <strong>Uploads</strong>.
            </p>
            <div v-if="handoffUpload" class="handoff-progress">
              <v-progress-linear :model-value="handoffUpload.progress" color="#64ffda" bg-color="#101820"
                height="8" rounded />
              <span class="handoff-progress-text">{{ handoffUpload.progress }}%</span>
            </div>
            <div class="step-actions handoff-actions">
              <v-btn class="primary-btn" @click="flyToSidebar">Close</v-btn>
            </div>
          </div>
        </div>
    </v-dialog>

    <ScanTable v-if="asset" ref="scanTable" :assetId="asset.id" @onGenerationStarted="generationStarted" />

    <v-dialog max-width="500" v-model="showResultDialog">
      <v-card class="result-dialog">
        <v-card-title class="result-title">{{ isSuccess ? 'Success' : 'Something went wrong' }}</v-card-title>
        <v-card-text class="result-text">{{ resultMessage }}</v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn class="primary-btn" @click="showResultDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch, defineEmits } from 'vue';
import LoadingButton from '@/components/buttons/LoadingButton.vue';
import ScanTable from '@/components/tables/ScanTable.vue';
import { useAssetStore } from '@/store/assetStore';
import { useUploadStore } from '@/store/uploadStore';
import { useAuth0 } from '@auth0/auth0-vue';

import camera360 from '@/assets/insta360-camera.png';
import camera2D from '@/assets/2d-camera.png';
import CaptureQualityPanel from '@/components/CaptureQualityPanel.vue';
import CaptureTips from '@/components/CaptureTips.vue';
import { readVideoMetadata } from '@/utils/videoMetadata';
import { extractFrames } from '@/utils/videoThumbnail';
import { gradeCapture, LEVEL } from '@/constants/captureQuality';

const PREVIEW_SEGMENTS = 5;              // 5 points spread across the video
const SEGMENT_WINDOW = 1;                // seconds of the real video played at each point

const emit = defineEmits(['OnScanCreated']);

const scanTable = ref(null);
const scanDialog = ref(false);

const assetStore = useAssetStore();
const asset = ref(null);

// Wizard panels: 1 Details, 2 Camera, 3 Capture (walkthrough/drone), 4 Upload, 5 Confirm.
const step = ref(1);
const stepLabelMap = { 1: 'Details', 2: 'Camera', 3: 'Capture', 4: 'Upload', 5: 'Confirm' };
const cameraType = ref(null);
const captureMethod = ref('Walkthrough');   // "Walkthrough" | "Drone"
const flow = computed(() => [1, 2, 3, 4, 5]);
const currentIndex = computed(() => Math.max(0, flow.value.indexOf(step.value)));
const { user } = useAuth0();

const scanName = ref('');
const scanDescription = ref('');
const selectedFile = ref(null);
const acceptedFileTypes = ref('');

// Capture-quality metadata (read locally from the chosen file, no upload/convert). _metaToken guards
// against a slow read landing after the user has swapped files.
const captureMeta = ref(null);
const captureMetaLoading = ref(false);
const captureFrames = ref([]);        // one still per segment [{ dataUrl } | null], for the thumbnail strip
const captureThumbLoading = ref(false);
let _metaToken = 0;

// First available still — shown instantly as the poster while the live preview builds.
const posterUrl = computed(() => captureFrames.value.find(f => f && f.dataUrl)?.dataUrl || null);
const hasAnyFrame = computed(() => captureFrames.value.some(f => f && f.dataUrl));
const segmentThumbs = computed(() =>
  Array.from({ length: PREVIEW_SEGMENTS }, (_, s) => ({ s, frame: captureFrames.value[s] || null })));

// One-line recap for the confirm step — the full traffic-light panel lives on the upload step now.
// Counts the graded metrics (info rows like codec/format don't count as pass/fail) and takes the
// worst level as the overall tone.
const captureRecap = computed(() => {
  if (captureMetaLoading.value) return { text: 'Checking capture quality…', tone: 'info' };
  const m = captureMeta.value;
  if (!m || !m.ok) return { text: 'Capture details not readable — it will still upload', tone: 'info' };
  const rows = gradeCapture(m, cameraType.value === '360' ? '360' : '2d');
  let good = 0, ok = 0, low = 0;
  rows.forEach((r) => {
    if (r.level === LEVEL.GOOD) good++;
    else if (r.level === LEVEL.OK) ok++;
    else if (r.level === LEVEL.LOW) low++;
  });
  const parts = [];
  if (good) parts.push(`${good} good`);
  if (ok) parts.push(`${ok} ok`);
  if (low) parts.push(`${low} to check`);
  const tone = low ? 'low' : ok ? 'ok' : 'good';
  return { text: `Capture quality — ${parts.join(' · ') || 'checked'}`, tone };
});

// Live preview: an off-DOM <video> (decoded + cycled through the 5 one-second segments) blitted to a
// <canvas> every frame with explicit rotation + contain-fit. Drawing it ourselves avoids the browser's
// inconsistent <video> rotation vs object-fit on rotated phone-portrait clips (which showed a cropped
// "top half"). While it loads, the first still + a "Building live preview…" message show.
const previewCanvas = ref(null);
const previewVideoReady = ref(false);
const previewBuilding = ref(false);
const previewFailed = ref(false);
const previewPlaying = ref(false);
const curSeg = ref(0);
let previewUrl = null;
let previewVideoEl = null;
let previewRaf = null;
let segTimes = [];
let liveRotation = 0;   // display rotation (deg) from the file's matrix; set by loadCaptureMeta

const drawPreviewFrame = () => {
  const v = previewVideoEl;
  const canvas = previewCanvas.value;
  if (!v || !canvas) return;
  const vw = v.videoWidth;
  const vh = v.videoHeight;
  if (!vw || !vh) return;

  const rot = ((liveRotation % 360) + 360) % 360;
  const quarter = rot === 90 || rot === 270;
  // File says rotated 90/270 but the browser still reports landscape dims → it isn't applying the
  // rotation to the decoded frame, so we rotate the canvas ourselves (else don't double-rotate).
  const needRotate = quarter && vw >= vh;
  const dispW = needRotate ? vh : vw;
  const dispH = needRotate ? vw : vh;

  // The canvas BUFFER is the media's display size, so the browser lays the canvas out at exactly the
  // media aspect (CSS: max-width/height + width/height:auto) — like an <img>. We fill the whole buffer,
  // so there's no letterbox and nothing clipped, regardless of the column width.
  if (canvas.width !== dispW || canvas.height !== dispH) { canvas.width = dispW; canvas.height = dispH; }
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, dispW, dispH);

  if (needRotate) {
    ctx.save();
    ctx.translate(dispW / 2, dispH / 2);         // buffer is vh×vw; rotate the vw×vh frame to fill it
    ctx.rotate((rot * Math.PI) / 180);
    ctx.drawImage(v, -vw / 2, -vh / 2, vw, vh);
    ctx.restore();
  } else {
    ctx.drawImage(v, 0, 0, dispW, dispH);
  }
};

const startDrawLoop = () => {
  if (previewRaf) return;
  const loop = () => { previewRaf = requestAnimationFrame(loop); drawPreviewFrame(); };
  loop();
};
const stopDrawLoop = () => { if (previewRaf) { cancelAnimationFrame(previewRaf); previewRaf = null; } };

const teardownLivePreview = () => {
  stopDrawLoop();
  const v = previewVideoEl;
  if (v) {
    try { v.pause(); } catch { /* ignore */ }
    v.onloadedmetadata = v.oncanplay = v.ontimeupdate = v.onended = v.onerror = null;
    v.removeAttribute('src');
    try { v.load(); } catch { /* ignore */ }
    previewVideoEl = null;
  }
  if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch { /* ignore */ } previewUrl = null; }
  segTimes = [];
  previewVideoReady.value = false;
  previewBuilding.value = false;
  previewFailed.value = false;
  previewPlaying.value = false;
  curSeg.value = 0;
};

const playPreview = () => {
  const v = previewVideoEl;
  if (!v) return;
  v.play().then(() => { previewPlaying.value = true; }).catch(() => { previewPlaying.value = false; });
};
const pausePreview = () => {
  const v = previewVideoEl;
  if (v) { try { v.pause(); } catch { /* ignore */ } }
  previewPlaying.value = false;
};
const togglePreview = () => { if (previewPlaying.value) pausePreview(); else playPreview(); };

const jumpToSegment = (s) => {
  curSeg.value = s;
  const v = previewVideoEl;
  if (previewVideoReady.value && v && segTimes.length) {
    try { v.currentTime = segTimes[s] || 0; } catch { /* ignore */ }
    playPreview();
  }
};

// Build the live preview: play ~1s at each of the 5 spread points, hop to the next, loop; blit to canvas.
const setupLivePreview = (file) => {
  teardownLivePreview();
  const canvas = previewCanvas.value;
  if (!file || !canvas) return;
  previewBuilding.value = true;
  previewUrl = URL.createObjectURL(file);

  const v = document.createElement('video');
  v.muted = true;
  v.playsInline = true;

  v.onloadedmetadata = () => {
    const dur = isFinite(v.duration) && v.duration > 0 ? v.duration : 0;
    segTimes = Array.from({ length: PREVIEW_SEGMENTS }, (_, s) => {
      const center = dur > 0 ? (dur * (s + 0.5)) / PREVIEW_SEGMENTS : 0;
      return Math.max(0, Math.min(center - SEGMENT_WINDOW / 2, Math.max(0, dur - SEGMENT_WINDOW)));
    });
  };
  v.oncanplay = () => {
    if (previewVideoReady.value) return;
    previewVideoReady.value = true;
    previewBuilding.value = false;
    curSeg.value = 0;
    try { v.currentTime = segTimes[0] || 0; } catch { /* ignore */ }
    playPreview();
    startDrawLoop();
  };
  v.ontimeupdate = () => {
    if (!segTimes.length) return;
    const start = segTimes[curSeg.value] || 0;
    if (v.currentTime >= start + SEGMENT_WINDOW || v.currentTime < start - 0.3) {
      curSeg.value = (curSeg.value + 1) % PREVIEW_SEGMENTS;
      try { v.currentTime = segTimes[curSeg.value] || 0; } catch { /* ignore */ }
    }
  };
  v.onended = () => {
    curSeg.value = 0;
    try { v.currentTime = segTimes[0] || 0; } catch { /* ignore */ }
    playPreview();
  };
  v.onerror = () => { previewFailed.value = true; previewBuilding.value = false; };

  previewVideoEl = v;
  v.src = previewUrl;
  try { v.load(); } catch { /* ignore */ }
};

onBeforeUnmount(teardownLivePreview);

// Result dialog is reused only for the "generation queued" confirmation now (uploads report via the sidebar).
const showResultDialog = ref(false);
const isSuccess = ref(true);
const resultMessage = ref('');

// Background upload hand-off.
const uploadStore = useUploadStore();
const handedOff = ref(false);
const closing = ref(false);   // playing the fly-to-sidebar close animation
const handoffName = ref('');
const lastUploadId = ref(null);
const handoffUpload = computed(() => uploadStore.uploads.find(u => u.id === lastUploadId.value) || null);

onMounted(() => {
  asset.value = assetStore.asset;
});

// The scan record is created in the background by the upload store, so refresh the table whenever an
// upload for this asset changes (appears / progresses / finishes) so the new scan shows up.
watch(
  () => uploadStore.uploads
    .filter(u => u.assetId === asset.value?.id)
    .map(u => `${u.id}:${u.status}:${u.scanId}`)
    .join('|'),
  () => { if (asset.value && scanTable.value) { refreshTable(); emit('OnScanCreated'); } },
);

const onFileSelected = async (file) => {
  const chosen = Array.isArray(file) ? (file[0] ?? null) : (file ?? null);
  selectedFile.value = chosen;
  await nextTick();              // ensure the preview canvas is mounted before we wire it up
  loadCaptureMeta(chosen);       // reads metadata (→ rotation) first, then starts the live preview
};

// Read capture metadata, then evenly-spread preview frames, from the selected file in the background.
// Metadata is read first so the frames can correct for the file's rotation flag (phone portrait videos);
// frames stream in (first one shows fast) and their decoded size sets the real 2D resolution.
const loadCaptureMeta = async (file) => {
  captureMeta.value = null;   // previewAspect (computed) falls back to 16/9 until the resolution is known
  captureFrames.value = new Array(PREVIEW_SEGMENTS).fill(null);
  liveRotation = 0;
  teardownLivePreview();         // stop the previous file's preview while this one's metadata is read
  if (!file) { captureMetaLoading.value = false; captureThumbLoading.value = false; return; }
  const token = ++_metaToken;
  captureMetaLoading.value = true;
  captureThumbLoading.value = true;

  let meta = null;
  try { meta = await readVideoMetadata(file); } catch { meta = { ok: false, sizeBytes: file.size }; }
  if (token !== _metaToken) return;
  captureMeta.value = meta;
  captureMetaLoading.value = false;
  liveRotation = (meta && meta.rotation) || 0;   // feed the live-canvas rotation (known before it starts)
  setupLivePreview(file);                         // start the smooth live preview now that rotation is set

  try {
    // One still per segment for the thumbnail strip + poster + resolution (the smooth motion comes
    // from the live <video>, not these stills).
    await extractFrames(file, {
      segments: PREVIEW_SEGMENTS,
      framesPerSegment: 1,
      rotation: (meta && meta.rotation) || 0,
    }, (i, frame) => {
      if (token !== _metaToken) return;
      const arr = captureFrames.value.slice();
      arr[i] = frame;
      captureFrames.value = arr;
      if (i === 0) {
        captureThumbLoading.value = false;   // first frame ready — reveal it
        // Use the decoded frame's orientation-correct size as the real resolution (2D only; a 360's
        // decoded frame is a single fisheye lens, so keep mp4box's stitched-equirect size there).
        if (cameraType.value !== '360' && frame.width && frame.height) {
          captureMeta.value = captureMeta.value && captureMeta.value.ok
            ? { ...captureMeta.value, width: frame.width, height: frame.height }
            : { ok: true, width: frame.width, height: frame.height, sizeBytes: file.size };
        }
      }
    });
  } catch { /* no preview */ }
  if (token === _metaToken) captureThumbLoading.value = false;
};

const selectCameraType = (type) => {
  cameraType.value = type;
  selectedFile.value = null;

  if (type === '2d') {
    acceptedFileTypes.value = '.mp4,.mov';
  } else {
    acceptedFileTypes.value = '.insv,.osv';
  }

  captureMethod.value = 'Walkthrough';   // default for the next page
  step.value = 3;   // ask walkthrough vs drone (both camera types)
};

// Back button on the upload step → the previous step (Capture — step 3 — for both single and dual lens).
const goToPrevious = () => {
  step.value = flow.value[Math.max(0, currentIndex.value - 1)];
};

const clearSelection = () => {
  selectedFile.value = null;
  captureMeta.value = null;
  captureFrames.value = [];
  teardownLivePreview();
  captureThumbLoading.value = false;
  captureMetaLoading.value = false;
  _metaToken++;
};

const resetForm = () => {
  step.value = 1;
  cameraType.value = null;
  captureMethod.value = 'Walkthrough';
  scanName.value = '';
  scanDescription.value = '';
  selectedFile.value = null;
  acceptedFileTypes.value = '';
  captureMeta.value = null;
  captureFrames.value = [];
  teardownLivePreview();
  captureThumbLoading.value = false;
  captureMetaLoading.value = false;
  _metaToken++;
};

const openScan = () => {
  resetForm();
  handedOff.value = false;
  scanDialog.value = true;
};

const cancelScan = () => {
  resetForm();
  handedOff.value = false;
  scanDialog.value = false;
};

// Hand the upload off to the background manager and show the "uploading in the background" screen —
// it keeps running even after the dialog is closed, surfaced in the sidebar Uploads widget.
const saveAndUpload = () => {
  handoffName.value = scanName.value;
  lastUploadId.value = uploadStore.enqueue({
    file: selectedFile.value,
    assetId: asset.value.id,
    assetName: asset.value.name,
    scanName: scanName.value,
    scanDescription: scanDescription.value,
    contentType: getContentType(selectedFile.value),
    numberOfLenses: cameraType.value === '360' ? 2 : 1,
    captureMethod: captureMethod.value,
    createdBy: user.value.name,
    createdBySub: user.value.sub,
    organizationId: asset.value.organizationId,
  });
  handedOff.value = true;
};

// Close the hand-off screen with a little "flies down to the sidebar" flourish, pulsing the sidebar
// Uploads widget so it's clear the upload lives there now.
const flyToSidebar = () => {
  if (closing.value) return;
  uploadStore.pulse();
  closing.value = true;
  setTimeout(() => {
    scanDialog.value = false;
    closing.value = false;
    resetForm();
    handedOff.value = false;
  }, 520);
};

const refreshTable = async () => {
  if (scanTable.value) await scanTable.value.refreshTable();
};

const getContentType = (file) => {
  if (file.type) return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith('.mp4')) return 'video/mp4';
  if (name.endsWith('.mov')) return 'video/quicktime';
  // .insv / .osv are camera-native containers with no standard MIME — send as binary.
  return 'application/octet-stream';
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${sizes[i]}`;
};

const generationStarted = async () => {
  isSuccess.value = true;
  resultMessage.value = 'Scan added to the processing queue. It will begin shortly — you can track its progress here.';
  showResultDialog.value = true;
  await refreshTable();
}
</script>

<style scoped>
.scan-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ----- Header row ----- */
.scans-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}

.scans-intro {
  margin: 0;
  font-size: 0.9rem;
  color: #8aa0aa;
  line-height: 1.5;
  max-width: 640px;
}

.scans-intro strong { color: #cfd8dc; font-weight: 700; }

.new-scan-btn {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 11px 20px; border-radius: 10px;
  background: var(--duallo-accent-gradient); color: #071514;
  border: none; font: inherit; font-weight: 700; cursor: pointer;
  transition: filter 0.15s ease;
}
.new-scan-btn:hover { filter: brightness(1.05); }

@media (max-width: 600px) {
  .new-scan-btn { width: 100%; justify-content: center; }
}

/* ----- Dialog card (matches the New asset / New version dialogs) ----- */
.modal {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: clamp(16px, calc(10px + 0.5vw), 22px);
  color: #fff;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* Never exceed the viewport — the step body scrolls instead of pushing the dialog off-screen
     (matters at 125–150% Windows display scaling). */
  max-height: 90vh;
  min-height: 0;
  box-sizing: border-box;
}
/* Reflow the wizard's inner grids on the DIALOG's own width (robust to 100–150% zoom). */
.scan-modal { container-type: inline-size; }

.modal-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex: 0 0 auto; }
.modal-heading { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.modal-head-right { display: flex; align-items: center; gap: 10px; flex: 0 0 auto; }

.modal-eyebrow {
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--duallo-accent);
}
.modal-title { margin: 3px 0 0; color: #fff; font-size: 1.3rem; font-weight: 700; }

.step-badge {
  font-size: 0.78rem; font-weight: 700; color: var(--duallo-accent); white-space: nowrap;
  background: rgba(100, 255, 218, 0.1); border: 1px solid rgba(100, 255, 218, 0.25);
  border-radius: 999px; padding: 4px 10px; font-variant-numeric: tabular-nums;
}
.modal-close {
  width: 34px; height: 34px; border-radius: 9px; background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08); color: #b0bec5; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto;
}
.modal-close:hover { background: rgba(255, 255, 255, 0.06); color: #fff; }

@container (max-width: 620px) {
  .camera-grid, .summary-grid { grid-template-columns: 1fr; }
}

/* ----- Step progress (custom replacement for v-stepper-header) ----- */
.step-progress {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.04);
  overflow-x: auto;
  scrollbar-width: none;
}

.step-progress::-webkit-scrollbar {
  display: none;
}

.step-dot {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  color: #8aa0aa;
  font-size: 0.85rem;
  font-weight: 600;
  flex: 0 0 auto;
  transition: background 0.15s ease, color 0.15s ease;
}

.step-dot.is-active {
  color: #cfd8dc;
}

.step-dot.is-current {
  background: rgba(100, 255, 218, 0.12);
  color: #ffffff;
  border: 1px solid rgba(100, 255, 218, 0.3);
}

.step-dot-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  font-size: 0.72rem;
  font-weight: 700;
  color: #8aa0aa;
}

.step-dot.is-active .step-dot-num {
  background: rgba(100, 255, 218, 0.18);
  color: var(--duallo-accent);
}

.step-dot.is-current .step-dot-num {
  background: var(--duallo-accent);
  color: #071514;
}

@media (max-width: 600px) {
  .step-dot-label {
    display: none;
  }

  .step-dot.is-current .step-dot-label {
    display: inline;
  }
}

/* ----- Stepper window ----- */
.scan-stepper {
  background: transparent;
  /* The scroll region: takes the remaining dialog height and scrolls tall steps (e.g. the Confirm
     step with the capture-quality panel) instead of overflowing the viewport. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin-right: -6px;   /* let the scrollbar sit in the padding gutter */
  padding-right: 6px;
  scrollbar-width: thin;                 /* Firefox */
  scrollbar-color: #3a4a52 transparent;
}
/* Match the app's universal scrollbar styling (AppLayout .scrollable-body, .anom-rail, .notes-scroll). */
.scan-stepper::-webkit-scrollbar { width: 6px; }
.scan-stepper::-webkit-scrollbar-track { background: transparent; }
.scan-stepper::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }
.scan-stepper::-webkit-scrollbar-thumb:hover { background: var(--duallo-accent); }

.step-card {
  padding: clamp(14px, calc(8px + 0.6vw), 20px);
  border-radius: 14px;
  background: linear-gradient(180deg, #243038 0%, #1f2a31 100%);
  border: 1px solid rgba(255, 255, 255, 0.04);
  display: flex;
  flex-direction: column;
  gap: clamp(14px, calc(8px + 0.5vw), 18px);
}

@media (max-width: 600px) {
  .step-card {
    padding: 16px 14px;
  }
}

.step-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.step-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--duallo-accent-muted);
}

.step-title {
  margin: 0;
  font-size: 1.2rem;
  color: #ffffff;
  font-weight: 700;
}

.step-description {
  margin: 0;
  color: #b0bec5;
  font-size: 0.92rem;
  line-height: 1.5;
}

.helper-card {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(100, 255, 218, 0.08);
  border: 1px solid rgba(100, 255, 218, 0.15);
  color: #cfe4e8;
  font-size: 0.88rem;
  align-self: flex-start;
}

.step-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .step-actions {
    flex-direction: column-reverse;
  }

  .step-actions .v-btn {
    width: 100%;
  }
}

.ghost-btn {
  background: rgba(255, 255, 255, 0.04) !important;
  color: #cfd8dc !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  text-transform: none !important;
}

.primary-btn {
  background: var(--duallo-accent-gradient) !important;
  color: #071514 !important;
  font-weight: 700 !important;
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0.02em;
}

/* ----- Camera selection cards ----- */
.camera-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

@media (max-width: 700px) {
  .camera-grid {
    grid-template-columns: 1fr;
  }
}

.camera-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  text-align: left;
  font: inherit;
  cursor: pointer;
  overflow: hidden;
  border-radius: 14px;
  background: linear-gradient(180deg, #20272d 0%, #181d22 100%);
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.camera-card:hover {
  transform: translateY(-3px);
  border-color: rgba(100, 255, 218, 0.25);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
}

.camera-card.is-selected {
  border-color: rgba(100, 255, 218, 0.6);
  box-shadow: 0 0 0 1px rgba(100, 255, 218, 0.2), 0 12px 28px rgba(0, 0, 0, 0.32);
}

.camera-image-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  background: #11161a;
  overflow: hidden;
}

.camera-image {
  width: 100%;
  height: 100%;
}

.camera-pill {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(16, 24, 30, 0.78);
  color: #eff8fb;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.camera-content {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.camera-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.camera-title {
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: 700;
}

.camera-text {
  color: #a9b7bd;
  line-height: 1.5;
  margin: 0;
  font-size: 0.88rem;
}

.camera-meta {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.camera-meta-chip {
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: #dbe5e9;
  font-size: 0.75rem;
  font-weight: 600;
}

/* ----- Capture-method cards (walkthrough / drone) ----- */
.capture-card {
  min-height: 150px;
}

.capture-body {
  padding: 20px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.capture-card .camera-title-row {
  width: 100%;
}

/* ----- Upload step ----- */
.upload-panel {
  padding: 16px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px dashed rgba(100, 255, 218, 0.18);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.upload-note-box {
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(100, 255, 218, 0.06);
  border: 1px solid rgba(100, 255, 218, 0.12);
}

.upload-note-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.92rem;
}

.upload-note {
  margin: 0;
  color: #a9bec4;
  font-size: 0.88rem;
  line-height: 1.5;
}

.selected-file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* ----- Upload step: LEFT = preview on top + file picker below · RIGHT = capture quality + tips ----- */
.upload-layout { display: grid; grid-template-columns: 1fr; gap: 24px; align-items: start; }
.upload-layout.has-file { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
.upload-left { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.upload-preview { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
/* Divider in the gutter between the preview and the quality columns. */
.upload-side {
  display: flex; flex-direction: column; gap: 14px; min-width: 0;
  padding-left: 24px;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

/* Keep the Vuetify file-input's selected-filename text inside the box (truncate long names). */
.upload-file-input :deep(.v-field__input) { flex-wrap: nowrap; min-width: 0; overflow: hidden; }
.upload-file-input :deep(.v-field__input > *) {
  min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
/* Stack to one column when the dialog is narrow (robust at 125–150% Windows scaling). The gutter
   divider becomes a horizontal rule above the quality column when stacked. */
@container (max-width: 620px) {
  .upload-layout.has-file { grid-template-columns: 1fr; }
  .upload-side {
    padding-left: 0; border-left: none;
    padding-top: 16px; border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
}

/* The frame hugs its content; the canvas/poster size themselves from their intrinsic buffer (= the
   media's display size) within the max box — like an <img> — so there are no letterbox bars. */
.preview-frame {
  position: relative;
  margin: 0 auto;
  display: grid;
  place-items: center;
  width: fit-content;        /* hug the media exactly — landscape wide, portrait tall/narrow */
  max-width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: #0d141a;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.preview-img,
.preview-canvas {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 230px;
}
/* Loading / empty states carry their own box so the frame isn't zero-sized before any media lands. */
.preview-shimmer { position: relative; inset: auto; width: min(100%, 300px); height: 150px; }
.preview-empty { min-width: 200px; min-height: 140px; justify-content: center; }
.preview-building {
  position: absolute; left: 50%; bottom: 8px; transform: translateX(-50%);
  display: flex; align-items: center; gap: 8px; white-space: nowrap;
  padding: 5px 12px; border-radius: 9999px;
  background: rgba(8, 12, 15, 0.78); border: 1px solid rgba(255, 255, 255, 0.12);
  color: #cfd8dc; font-size: 0.76rem;
}
.preview-empty {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  color: #8aa0aa; font-size: 0.84rem; text-align: center; padding: 0 16px;
}
.preview-shimmer {
  background: linear-gradient(100deg,
    rgba(255, 255, 255, 0.03) 30%,
    rgba(255, 255, 255, 0.08) 50%,
    rgba(255, 255, 255, 0.03) 70%);
  background-size: 200% 100%;
  animation: previewShimmer 1.2s ease-in-out infinite;
}
@keyframes previewShimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.preview-caption { display: flex; align-items: center; gap: 8px; min-width: 0; font-size: 0.82rem; }
.preview-name { font-weight: 600; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.preview-size { color: #8aa0aa; flex: 0 0 auto; margin-left: auto; }

/* Flipbook play/pause overlay on the preview. */
.flip-toggle {
  position: absolute; bottom: 8px; right: 8px;
  width: 30px; height: 30px; display: grid; place-items: center;
  border-radius: 50%; cursor: pointer;
  background: rgba(8, 12, 15, 0.7); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.flip-toggle:hover { background: rgba(8, 12, 15, 0.92); border-color: var(--duallo-accent); color: var(--duallo-accent); }

/* Clickable strip of frames spread across the video. */
.frame-strip { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; }
.frame-thumb {
  position: relative; aspect-ratio: 16 / 10; overflow: hidden;
  border-radius: 6px; padding: 0; cursor: pointer;
  background: #0d141a; border: 1px solid rgba(255, 255, 255, 0.1);
}
.frame-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.frame-thumb:hover:not(.active):not(:disabled) { border-color: rgba(255, 255, 255, 0.28); }
.frame-thumb.active { border-color: var(--duallo-accent); box-shadow: 0 0 0 1px var(--duallo-accent); }
.frame-thumb.empty { cursor: default; }
.frame-thumb-shimmer {
  position: absolute; inset: 0;
  background: linear-gradient(100deg,
    rgba(255, 255, 255, 0.03) 30%, rgba(255, 255, 255, 0.08) 50%, rgba(255, 255, 255, 0.03) 70%);
  background-size: 200% 100%;
  animation: previewShimmer 1.2s ease-in-out infinite;
}

.selected-file-name {
  color: #ffffff;
  font-weight: 600;
  line-height: 1.3;
  word-break: break-word;
}

.selected-file-meta {
  color: #8aa0aa;
  font-size: 0.82rem;
  margin-top: 2px;
}

/* ----- Confirm step: compact summary review + a one-line capture-quality recap ----- */
.confirm-col-head {
  font-size: 0.82rem; font-weight: 700; color: #d6e4ea; letter-spacing: 0.02em; margin-bottom: 10px;
}

.review-list { margin: 0; display: flex; flex-direction: column; }
.review-row {
  display: flex; flex-direction: column; gap: 2px;
  padding: 9px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.review-row:last-child { border-bottom: none; }
.review-row dt {
  font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.06em; color: #8aa0aa; font-weight: 700;
}
.review-row dd {
  margin: 0; color: #fff; font-size: 0.9rem; font-weight: 600; word-break: break-word;
}
.review-file { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.82rem; }

/* One-line capture-quality recap (full panel is on the Upload step). */
.confirm-recap {
  margin-top: 14px;
  display: flex; align-items: center; gap: 9px;
  padding: 10px 13px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #d6e4ea; font-size: 0.85rem; font-weight: 600;
}
.confirm-recap .recap-dot {
  flex: 0 0 auto; width: 9px; height: 9px; border-radius: 50%; background: #8aa0aa;
}
.confirm-recap.tone-good .recap-dot { background: #6ee7a0; }
.confirm-recap.tone-ok .recap-dot { background: #ffb45c; }
.confirm-recap.tone-low .recap-dot { background: #ff8a80; }
.confirm-recap.tone-low { border-color: rgba(255, 138, 128, 0.35); }

/* Capture-quality in the right column: one compact single-line card per row ("icon · label · value"),
   full column width — the side panel is too narrow for two columns (long values like 7680×3840 would
   overflow). Stacked, they stay short enough to avoid a scrollbar. */
.upload-side :deep(.cq) { gap: 8px; }
.upload-side :deep(.cq-grid) { grid-template-columns: 1fr; gap: 8px; }
.upload-side :deep(.cq-card) {
  flex-direction: row; align-items: center; gap: 8px;
  padding: 7px 11px; min-width: 0;
}
.upload-side :deep(.cq-card-label) { white-space: nowrap; }
.upload-side :deep(.cq-card-value) {
  margin-left: auto; font-size: 0.86rem; white-space: nowrap; flex: 0 0 auto;
}

/* ----- Hand-off (upload continues in the background) ----- */
.handoff-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 28px 24px 22px;
}
.handoff-icon {
  width: 60px; height: 60px; border-radius: 16px;
  background: rgba(74, 222, 128, 0.14); border: 1px solid rgba(74, 222, 128, 0.3);
  display: flex; align-items: center; justify-content: center;
}
.handoff-title { margin: 4px 0 0; font-size: 1.25rem; font-weight: 700; color: #fff; }
.handoff-text { margin: 0; font-size: 0.92rem; color: #b0bec5; line-height: 1.5; max-width: 420px; }
.handoff-text strong { color: var(--duallo-accent-muted); }
.handoff-progress {
  display: flex; align-items: center; gap: 10px; width: 100%; max-width: 420px; margin-top: 4px;
}
.handoff-progress :deep(.v-progress-linear) { flex: 1 1 auto; }
.handoff-progress-text { flex: 0 0 auto; font-size: 0.85rem; font-weight: 700; color: var(--duallo-accent-muted); font-variant-numeric: tabular-nums; }
.handoff-actions { margin-top: 10px; justify-content: center; }

/* Close flourish: the dialog shrinks and flies toward the bottom-left (where the sidebar Uploads
   widget lives) before the dialog actually closes. */
.scan-modal { transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease; }
.scan-modal--flying {
  transform-origin: bottom left;
  transform: translate(-42vw, 42vh) scale(0.12);
  opacity: 0;
  pointer-events: none;
}
@media (prefers-reduced-motion: reduce) {
  .scan-modal { transition: opacity 0.2s ease; }
  .scan-modal--flying { transform: none; }
}

/* ----- Table shell ----- */
.table-shell {
  padding: 20px 22px;
  border-radius: 14px;
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
}

.table-shell-header {
  margin-bottom: 14px;
}

.table-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
  margin-bottom: 2px;
}

.table-title {
  margin: 0;
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 700;
}

@media (max-width: 600px) {
  .table-shell {
    padding: 16px 14px;
    border-radius: 12px;
  }
}

/* ----- Result dialog ----- */
.result-dialog {
  border-radius: 14px !important;
  background: var(--duallo-card-gradient) !important;
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: #fff;
}

.result-title {
  color: #ffffff;
  font-weight: 700;
  padding: 18px 20px 6px;
}

.result-text {
  color: #b2c0c5;
  padding: 0 20px 12px;
}
</style>
